import INVENTORY_API_BASE_URL from "../../config/api";
import { apiFetch } from "../../config/apiClient";
import React, { useEffect, useState } from "react";
import styles from "./Sku.module.css";
import Table from "react-bootstrap/Table";

function Sku() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSource, setSelectedSource] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiFetch(
          `${INVENTORY_API_BASE_URL}/api/get-barcode-summary`,
        );
        const result = await res.json();

        if (result.success && Array.isArray(result.data)) {
          const today = new Date();

          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(today.getMonth() - 3);

          const filtered = result.data.filter((item) => {
            const itemDate = new Date(item.date.split("-").reverse().join("-"));

            return itemDate >= threeMonthsAgo && itemDate <= today;
          });

          setData(filtered);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const uniqueSources = ["All", ...new Set(data.map((item) => item.source))];

  const filteredData = data
    .filter((item) =>
      selectedSource !== "All" ? item.source === selectedSource : true,
    )
    .filter((item) => {
      if (!startDate && !endDate) return true;

      const itemDate = new Date(item.date.split("-").reverse().join("-"));
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;

      return true;
    });

  if (loading) {
    return (
      <main className={styles.loading}>
        <div className={styles.loadingCard} role="status">
          <span className={styles.loadingSpinner} aria-hidden="true" />
          <span>
            <strong>Loading SKU summary</strong>
            <small>Retrieving recent barcode activity</small>
          </span>
        </div>
      </main>
    );
  }

  const handleCSVDownload = () => {
    if (!data || data.length === 0) return;

    const rows = [];
    data.forEach((item) => {
      item.matchedTitles?.forEach((mt) => {
        rows.push({
          Source: item.source,
          Date: item.date,
          SKU: mt.prefix,
          Count: mt.count,
          Title: mt.title,
          Medium: mt.Medium,
        });
      });
    });

    const header = Object.keys(rows[0]).join(",");
    const csvContent =
      header +
      "\n" +
      rows
        .map((r) =>
          Object.values(r)
            .map((v) => `"${v}"`)
            .join(","),
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `B2C_SKU_Summary_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
  };

  const totalBooksInRange = filteredData.reduce(
    (total, item) => total + (item.barcodeData?.length || 0),
    0,
  );

  const matchingSkuItems = (selectedItem?.matchedTitles || [])
    .filter((item) =>
      item.prefix.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => b.count - a.count);

  return (
    <main className={styles.skuwiseContainer}>
      <div className={styles.content}>
        <header className={styles.hero}>
          <div className={styles.heroCopy}>
            <span className={styles.eyebrow}>
              <span className={styles.eyebrowMark} aria-hidden="true" />
              B2C INVENTORY
            </span>
            <h1 className={styles.heading}>SKU summary</h1>
            <p className={styles.heroDescription}>
              Review barcode volume by platform, date, and SKU across the last
              three months.
            </p>
          </div>
          <div className={styles.heroBadge}>
            <span className={styles.heroBadgeIcon} aria-hidden="true">◷</span>
            <span>
              <small>Data window</small>
              <strong>Last 3 months</strong>
            </span>
          </div>
        </header>

        <section className={styles.metrics} aria-label="Summary totals">
          <article className={styles.metricCard}>
            <span className={styles.metricIcon} aria-hidden="true">01</span>
            <div className={styles.metricCopy}>
              <span>Summary records</span>
              <strong>{filteredData.length.toLocaleString()}</strong>
              <small>Matching the active filters</small>
            </div>
          </article>
          <article className={styles.metricCard}>
            <span className={styles.metricIconTeal} aria-hidden="true">02</span>
            <div className={styles.metricCopy}>
              <span>Total books</span>
              <strong>{totalBooksInRange.toLocaleString()}</strong>
              <small>Books in the selected date range</small>
            </div>
          </article>
          <article className={styles.metricCard}>
            <span className={styles.metricIconViolet} aria-hidden="true">03</span>
            <div className={styles.metricCopy}>
              <span>Platforms</span>
              <strong>{new Set(filteredData.map((item) => item.source)).size.toLocaleString()}</strong>
              <small>Represented in these records</small>
            </div>
          </article>
        </section>

        <section className={styles.filterCard} aria-labelledby="sku-filters-title">
          <div className={styles.sectionHeading}>
            <div>
              <span className={styles.sectionKicker}>REFINE THE REGISTER</span>
              <h2 id="sku-filters-title">Filters and export</h2>
            </div>
            <span className={styles.filterHint}>
              {filteredData.length.toLocaleString()} matching records
            </span>
          </div>

          <div className={styles.filterGrid}>
            <label className={styles.filterField} htmlFor="sku-source-filter">
              <span>Platform</span>
              <select
                id="sku-source-filter"
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
              >
                {uniqueSources.map((src, i) => (
                  <option key={i} value={src}>
                    {src === "All" ? "All platforms" : src}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.filterField} htmlFor="sku-start-date">
              <span>Date from</span>
              <input
                id="sku-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>

            <label className={styles.filterField} htmlFor="sku-end-date">
              <span>Date to</span>
              <input
                id="sku-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>

            <div className={styles.filterActions}>
              <button
                type="button"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
                className={styles.clearBtn}
              >
                Clear dates
              </button>
              <button
                type="button"
                onClick={handleCSVDownload}
                className={styles.csvBtn}
              >
                <span aria-hidden="true">↓</span>
                Export CSV
              </button>
            </div>
          </div>
        </section>

        <section className={styles.tableCard} aria-labelledby="sku-table-title">
          <div className={styles.tableHeading}>
            <div>
              <span className={styles.sectionKicker}>RECENT ACTIVITY</span>
              <h2 id="sku-table-title">Barcode summaries</h2>
              <p>Open a record to inspect its SKU quantities and book details.</p>
            </div>
            <span className={styles.recordCount}>
              {filteredData.length.toLocaleString()} records
            </span>
          </div>

          <div className={styles.tableWrapper}>
            <Table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">No.</th>
                  <th scope="col">Platform</th>
                  <th scope="col">Date</th>
                  <th scope="col">Total books</th>
                  <th scope="col">SKU breakdown</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <tr
                      key={index}
                    >
                      <td className={styles.numberCell}>{String(index + 1).padStart(2, "0")}</td>
                      <td>
                        <span className={styles.sourceTag}>{item.source}</span>
                      </td>
                      <td className={styles.dateCell}>{item.date}</td>
                      <td>
                        <strong className={styles.bookCount}>
                          {(item.barcodeData?.length || 0).toLocaleString()}
                        </strong>
                        <span className={styles.bookUnit}>books</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => setSelectedItem(item)}
                          className={styles.detailsBtn}
                        >
                          View SKU details
                          <span aria-hidden="true">→</span>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className={styles.noData}>
                      <span className={styles.emptyIcon} aria-hidden="true">⌕</span>
                      <strong>No summaries found</strong>
                      <span>Try a different platform or adjust the date range.</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className={styles.tableFooter}>
            <span>
              Showing <strong>{filteredData.length.toLocaleString()}</strong> summary records
            </span>
            <span>Updated from recent barcode activity</span>
          </div>
        </section>
      </div>

      {selectedItem && (
        <div className={styles.overlay}>
          <section
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="sku-modal-title"
          >
            <header className={styles.modalHeader}>
              <div className={styles.modalHeading}>
                <span className={styles.sectionKicker}>SKU BREAKDOWN</span>
                <h2 id="sku-modal-title">{selectedItem.source}</h2>
                <p>{selectedItem.date}</p>
              </div>
              <div className={styles.modalTotal}>
                <span>Total books</span>
                <strong>{(selectedItem.barcodeData?.length || 0).toLocaleString()}</strong>
              </div>
            </header>

            <div className={styles.modalBody}>
              <label className={styles.searchField} htmlFor="sku-detail-search">
                <span>Find a SKU</span>
                <input
                  id="sku-detail-search"
                  type="text"
                  placeholder="Search by SKU code"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </label>

              <div className={styles.modalList}>
                {matchingSkuItems.length > 0 ? (
                  matchingSkuItems.map((item, i) => (
                    <article key={i} className={styles.skuCard}>
                      <div className={styles.skuCardHeading}>
                        <strong>{item.prefix}</strong>
                        <span>{item.count} units</span>
                      </div>
                      <p className={styles.bookTitle}>{item.title}</p>
                      <span className={styles.bookMedium}>
                        Medium <strong>{item.Medium}</strong>
                      </span>
                    </article>
                  ))
                ) : (
                  <div className={styles.noSkuResults}>
                    No SKU matches this search.
                  </div>
                )}
              </div>
            </div>

            <footer className={styles.modalFooter}>
              <button
                type="button"
                onClick={() => {
                  setSelectedItem(null);
                  setSearchTerm("");
                }}
                className={styles.closeBtn}
              >
                Close details
              </button>
            </footer>
          </section>
        </div>
      )}
    </main>
  );
}

export default Sku;
